import React, { useState, useEffect, useRef } from "react";
import { useLoading } from "../../contexts/LoadingContext";
import { showToast } from "../../components/Toaster";
import JobService from "../../services/JobService";
import { TripContainer } from "../../interfaces/job.interface";
import Modal from "../Modal/Modal";
import { useMetadata } from "../../contexts/MetadataContext";
import CustomSelect, { CustomSelectRef } from "../CoreComponents/CustomSelect";
import ContainerType from "../ContainerType/ContainerType";
import {
  ModalSection,
  ModalInfoItem,
  ModalInfoGrid,
  ModalFormGroup,
  ModalButton,
  ModalError,
  ModalFooter,
  ModalIcons,
} from "../Modal/ModalLayout";

interface UpdateContainerModalProps {
  isOpen: boolean;
  onClose: () => void;
  containerData: {
    jobBreakupDetailId: number;
    containerNumber: string;
    containerSize: number;
    containerType: string;
    driverIdName?: string;
    transporterIdName?: string;
    truckIdName?: string;
    jobCode?: string;
    source?: string;
    destination?: string;
  } | null;
  onUpdateSuccess: () => void;
  existingContainerNumbers?: Set<string>;
}

const UpdateContainerModal: React.FC<UpdateContainerModalProps> = ({
  isOpen,
  onClose,
  containerData,
  onUpdateSuccess,
  existingContainerNumbers,
}) => {
  const [containerNumber, setContainerNumber] = useState("");
  const [containerSize, setContainerSize] = useState<string>("");
  const [containerType, setContainerType] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [showValidationError, setShowValidationError] = useState(false);
  const loader = useLoading();
  const { containerTypes } = useMetadata();
  const sizeSelectRef = useRef<CustomSelectRef>(null);
  const typeSelectRef = useRef<CustomSelectRef>(null);

  useEffect(() => {
    if (containerData && isOpen) {
      // Handle null container number
      // setContainerNumber(containerData.containerNumber || "");

      // Convert container size from text format to numeric value
      let sizeValue = "20"; // Default value

      if (containerData.containerSize) {
        const sizeText = containerData.containerSize.toString().toUpperCase();
        if (sizeText.includes("TWENTY")) {
          sizeValue = "20";
        } else if (sizeText.includes("FORTY")) {
          sizeValue = "40";
        } else if (
          sizeText.includes("FORTY-FIVE") ||
          sizeText.includes("FORTYFIVE")
        ) {
          sizeValue = "45";
        } else if (!isNaN(Number(containerData.containerSize))) {
          // If it's already a number, use it directly
          sizeValue = containerData.containerSize.toString();
        }
      }

      const validSizes = ["20", "40", "45"];
      const validSize = validSizes.includes(sizeValue) ? sizeValue : "20";

      setContainerSize(validSize);
      setContainerType(containerData.containerType || "");

      // Set initial values for dropdowns
      if (sizeSelectRef.current) {
        sizeSelectRef.current.setSeletedOption(validSize);
      }
      if (typeSelectRef.current) {
        typeSelectRef.current.setSeletedOption(
          containerData.containerType || ""
        );
      }

      // Reset validation state
      setError(null);
      setShowValidationError(false);
    }
  }, [containerData, isOpen]);

  const validateContainerNumber = (number: string) => {
    // Container number format: 4 letters followed by 7 numbers
    const containerRegex = /^[A-Z]{4}\d{7}$/;
    return containerRegex.test(number.toUpperCase());
  };

  const handleContainerNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value.toUpperCase();
    setContainerNumber(value);

    // Clear validation error when user starts typing
    if (showValidationError) {
      setShowValidationError(false);
    }
  };

  const handleSizeChange = (value: string | string[]) => {
    setContainerSize(value as string);
  };

  const handleTypeChange = (value: string | string[]) => {
    setContainerType(value as string);
  };

  const handleUpdateContainer = async () => {
    if (!containerData) return;

    // Reset error state
    setError(null);
    setShowValidationError(false);

    // Validate container number
    const trimmedNumber = containerNumber.trim();
    if (!trimmedNumber) {
      setShowValidationError(true);
      setError("Container number is required");
      return;
    }

    if (!validateContainerNumber(trimmedNumber)) {
      setShowValidationError(true);
      setError("Invalid container number format");
      return;
    }

    // Check if container number already exists (excluding the current container being updated)
    if (
      existingContainerNumbers &&
      existingContainerNumbers.has(trimmedNumber) &&
      containerData.containerNumber !== trimmedNumber
    ) {
      setShowValidationError(true);
      setError("Container number already exists in this job");
      return;
    }

    try {
      loader.setLoading(true);

      // Create a trip container object for the API
      const tripContainer: TripContainer = {
        tripId: containerData.jobBreakupDetailId,
        containerNumber: trimmedNumber,
      };

      const response = await JobService.assignContainers([tripContainer]);
      if (response.data.error) {
        showToast(response.data.error, "error");
      } else {
        showToast("Container updated successfully", "success");
        onUpdateSuccess();
        onClose();
      }
    } catch (error) {
      console.error("Error updating container:", error);
      showToast("Failed to update container", "error");
    } finally {
      loader.setLoading(false);
    }
  };

  const isFormValid = () => {
    return !!containerNumber.trim() && validateContainerNumber(containerNumber);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Update Container Number"
      className="component_modal_container update_container_modal"
      size="lg"
      footer={
        <ModalFooter>
          <ModalButton variant="secondary" onClick={onClose}>
            Cancel
          </ModalButton>
          <ModalButton
            variant="primary"
            onClick={handleUpdateContainer}
            disabled={!isFormValid()}
          >
            Update Container
          </ModalButton>
        </ModalFooter>
      }
    >
      <div className="space-y-6">
        {/* Job Information */}
        {/* {containerData?.jobCode && (
          <ModalSection title="Job Information" icon={ModalIcons.Info}>
            <ModalInfoGrid>
              <ModalInfoItem label="Job Code" value={containerData.jobCode} />
              <ModalInfoItem
                label="Trip ID"
                value={containerData.jobBreakupDetailId}
              />
            </ModalInfoGrid>
          </ModalSection>
        )} */}

        {/* Current Container Information */}
        <ModalSection
          title="Current Container Details"
          // icon={ModalIcons.Container}
        >
          <ModalInfoGrid>
            <ModalInfoItem
              label="Container Number"
              value={containerData?.containerNumber || "N/A"}
            />
            <ModalInfoItem
              label="Type"
              value={
                <ContainerType
                  size={Number(containerSize)}
                  type={containerType}
                />
              }
            />
          </ModalInfoGrid>
        </ModalSection>

        {/* Container Update Form */}
        <ModalSection title="Update Container Details">
          <div className="space-y-4">
            <div className="form-group">
              <div className="form-field">
                <label className="required_field_label">
                  New Container Number
                </label>
                <input
                  maxLength={11}
                  type="text"
                  value={containerNumber}
                  onChange={handleContainerNumberChange}
                  placeholder="Enter container number (e.g., ABCD1234567)"
                  className={showValidationError ? "invalid_field" : ""}
                  autoFocus
                />
              </div>
              <div className="form-help">
                Format: 4 letters + 7 digits (e.g., ABCD1234567)
              </div>
            </div>

            {/* <div className="grid grid-cols-2 gap-4">
              <ModalFormGroup label="Container Size" required>
                <CustomSelect
                  ref={sizeSelectRef}
                  options={[
                    { value: "20", label: "20" },
                    { value: "40", label: "40" },
                    { value: "45", label: "45" },
                  ]}
                  onChange={handleSizeChange}
                  placeholder="Choose size"
                  label="Container Size"
                  className="w-full"
                />
              </ModalFormGroup>
              <ModalFormGroup label="Container Type" required>
                <CustomSelect
                  ref={typeSelectRef}
                  options={
                    containerTypes?.map((type: any) => ({
                      value: typeof type === "string" ? type : type.value,
                      label: typeof type === "string" ? type : type.label,
                    })) || [
                      { value: "GP", label: "GP" },
                      { value: "HQ", label: "HQ" },
                      { value: "OT", label: "OT" },
                      { value: "FL", label: "FL" },
                      { value: "RF", label: "RF" },
                    ]
                  }
                  onChange={handleTypeChange}
                  placeholder={
                    containerTypes ? "Choose type" : "Loading types..."
                  }
                  label="Container Type"
                  className="w-full"
                  disabled={!containerTypes}
                />
              </ModalFormGroup>
            </div> */}
          </div>
        </ModalSection>

        {/* Driver & Transporter Information */}
        {/* {(containerData?.driverIdName || containerData?.transporterIdName) && (
          <ModalSection
            title="Driver & Transporter Details"
            icon={ModalIcons.User}
          >
            <ModalInfoGrid>
              {containerData?.driverIdName && (
                <ModalInfoItem
                  label="Driver"
                  value={containerData.driverIdName}
                />
              )}
              {containerData?.truckIdName && (
                <ModalInfoItem
                  label="Truck"
                  value={containerData.truckIdName}
                />
              )}
              {containerData?.transporterIdName && (
                <ModalInfoItem
                  label="Transporter"
                  value={containerData.transporterIdName}
                  className="col-span-2"
                />
              )}
            </ModalInfoGrid>
          </ModalSection>
        )} */}

        {/* Route Information */}
        {/* {(containerData?.source || containerData?.destination) && (
          <ModalSection title="Route Information" icon={ModalIcons.Location}>
            <ModalInfoGrid>
              {containerData?.source && (
                <ModalInfoItem label="Source" value={containerData.source} />
              )}
              {containerData?.destination && (
                <ModalInfoItem
                  label="Destination"
                  value={containerData.destination}
                />
              )}
            </ModalInfoGrid>
          </ModalSection>
        )} */}

        {/* Error Message */}
        {showValidationError && error && <ModalError message={error} />}
      </div>
    </Modal>
  );
};

export default UpdateContainerModal;
